import type { NewsArticleCard, NewsReference } from '@/api/news/types';
import type { PreviewSelection } from '@/components/sources/SourcePreviewDrawer';
import { officialSources } from '@/content/sources';
import { clusterLabel } from '@/components/sources/sourceMeta';
import { NEWS_FEEDBACK_EMAIL } from './editorial';

/** Gli indirizzi della sezione, senza slash finale come tutto il sito. */
export const NEWS_PATH = '/news';
export const newsArticlePath = (slug: string) => `${NEWS_PATH}/${slug}`;
export const newsCategoryPath = (slug: string) => `${NEWS_PATH}/categoria/${slug}`;
export const NEWS_FEED_PATH = `${NEWS_PATH}/feed.xml`;

/** L'`<article>` della pagina: il dock ci legge quanto se ne è letto, senza un ref che attraversi il server. */
export const NEWS_ARTICLE_ID = 'news-article';

/**
 * Le immagini di condivisione, rese dal server come le pagine (`lib/news/og`):
 * una per l'indice, una per categoria, una per articolo. `og.png` è quindi uno
 * slug riservato, come `categoria`.
 */
export const NEWS_OG_SIZE = { width: 1200, height: 630 } as const;
export const NEWS_OG_PATH = `${NEWS_PATH}/og.png`;
export const newsArticleOgPath = (slug: string) => `${newsArticlePath(slug)}/og.png`;
export const newsCategoryOgPath = (slug: string) => `${newsCategoryPath(slug)}/og.png`;

/**
 * Su mobile il testo ha bisogno di tutta la larghezza. Nel resto del sito
 * `Section` e `Container` aggiungono 24px ciascuno, cioè 48 per lato. Nelle
 * news, sotto `sm`, la sezione tiene 16px e il contenitore nessuno; da `sm` in
 * su si torna ai margini di sempre. Stanno qui e non accanto ai componenti
 * perché un file `'use client'` non esporta stringhe ai Server Components.
 */
export const SECTION_GUTTER = 'px-4 sm:px-6';
export const MOBILE_GUTTER = 'px-0 sm:px-6';

const LONG_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };

/** «19 settembre 2026», da una data ISO o YYYY-MM-DD. Fuso di Roma: è un sito italiano. */
export function formatNewsDate(value: string | null | undefined): string {
  if (!value) return '';
  // Una data senza ora va letta a mezzogiorno, o col fuso scivola al giorno prima
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('it-IT', { ...LONG_DATE, timeZone: 'Europe/Rome' }).format(date);
}

/**
 * Un percorso del sito o un URL già assoluto → URL assoluto (og:image, JSON-LD,
 * feed). Il dominio è quello di produzione anche in locale, come nei canonical
 * e in `structuredData`: sono indirizzi che si dichiarano, non che si visitano.
 */
const SITE_URL = 'https://www.trovanorma.it';

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/**
 * «Segnala un errore»: un'email già intestata all'articolo, col suo indirizzo
 * nel corpo, così chi scrive non deve spiegare di quale pezzo parla.
 */
export function errorReportMailto(article: Pick<NewsArticleCard, 'title' | 'slug'>): string {
  const subject = encodeURIComponent(`Segnalazione: ${article.title}`);
  const body = encodeURIComponent(
    `Articolo: ${absoluteUrl(newsArticlePath(article.slug))}\n\nCosa non torna:\n`
  );
  return `mailto:${NEWS_FEEDBACK_EMAIL}?subject=${subject}&body=${body}`;
}

// ───────────────────────── citazioni `doc://` ─────────────────────────

const DOC_SCHEME = 'doc://';

export interface DocLink {
  publicId: string;
  /** `num` dell'articolo citato, già decodificato (es. «Art. 116») */
  anchor: string | null;
}

/**
 * Lo stesso schema delle risposte in piattaforma: `doc://<public_id>#<ancora>`.
 * La forma arricchita dell'app (`doc://<public_id>/<id>`) è tollerata: vince
 * quello che sta prima della barra.
 */
export function parseDocHref(href: string | undefined): DocLink | null {
  if (!href?.startsWith(DOC_SCHEME)) return null;
  const [target, rawAnchor] = href.slice(DOC_SCHEME.length).split('#');
  const publicId = target.split('/')[0];
  if (!publicId) return null;
  let anchor: string | null = null;
  if (rawAnchor) {
    try {
      anchor = decodeURIComponent(rawAnchor);
    } catch {
      anchor = rawAnchor;
    }
  }
  return { publicId, anchor };
}

export const isDocHref = (href: string) => href.startsWith(DOC_SCHEME);

/**
 * Il catalogo di `/fonti` distingue tre Cassazioni (civile, penale, lavoro) là
 * dove il documento ha un solo cluster: la sezione la dice la categoria.
 */
function sourceIdFor(reference: Pick<NewsReference, 'cluster' | 'category'>): string {
  const { cluster, category } = reference;
  if (cluster?.startsWith('italgiure_cassazione')) {
    if (category?.includes('lavoro')) return 'italgiure_cassazione_lavoro';
    if (category?.includes('penale')) return 'italgiure_cassazione_penale';
    return 'italgiure_cassazione_civile';
  }
  if (cluster === 'eurlex') {
    return category?.includes('sentenz') ? 'eurlex_giurisprudenza' : 'eurlex_normativa';
  }
  return cluster ?? '';
}

/** Da una fonte citata a quello che serve al pannello di `/fonti` per aprirsi. */
export function selectionForReference(reference: NewsReference): PreviewSelection {
  const sourceId = sourceIdFor(reference);
  const source = officialSources.find(candidate => candidate.id === sourceId);
  return {
    publicId: reference.public_id,
    sourceId,
    sourceName: source?.name ?? (clusterLabel(reference.cluster) || 'Fonte ufficiale'),
    sourceInitials: source?.initials ?? '§',
    documentName: reference.name ?? 'Documento',
    category: reference.category,
    date: reference.date,
    externalLink: reference.external_link,
  };
}

/**
 * «Art. 116» → «116», «Art. 3 bis» → «3 bis»: dopo «art.» o «artt.» basta il
 * numero. Il prefisso può essere ripetuto nei dati («Art. Art. 79»): va tolto
 * tutto.
 */
export const articleNumber = (anchor: string) => anchor.replace(/^(\s*art(icolo|t)?\.?\s*)+/i, '');

// ───────────────────────── indice dell'articolo ─────────────────────────

/** Stesso id nel componente che rende l'`<h2>` e nell'indice che ci punta. */
export function headingId(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface ArticleHeading {
  id: string;
  text: string;
}

/** I titoli `##` del testo, per l'indice. Il Markdown in linea viene tolto. */
export function extractHeadings(markdown: string): ArticleHeading[] {
  return markdown
    .split('\n')
    .filter(line => /^##\s+/.test(line))
    .map(line =>
      line
        .replace(/^##\s+/, '')
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        .replace(/[*_`]/g, '')
        .trim()
    )
    .map(text => ({ id: headingId(text), text }));
}

/** Testo semplice da Markdown: per il JSON-LD delle FAQ e le descrizioni. */
export function stripMarkdown(markdown: string): string {
  return markdown
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[*_`>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
